import React from 'react';

import {Box} from '@material-ui/core';
import {useSnackbar} from 'notistack';
import {useHistory} from 'react-router-dom';

import {
    EfeAccordionValues,
    Field,
    DigitalizedSignatureValues,
    EfeResponse,
    UploadDocumentServiceRequest,
    Documents,
} from 'commons/models';
import {Constants, HistoryRoutes, Messages, SnackbarVariants, ViewKeys} from 'commons/constants';
import {EfeAccordion} from 'components';
import {DocumentServices} from 'services';
import {MainContent} from 'layouts/commons';
import DocsSignForm from './docs-sign-form/DocsSignForm';
import {useAlert, useFlowFieldSearch, useSpinner} from 'hooks';
import {getBase64Extension, nextStep} from 'commons/utils/Functions';
import {useFlowContext} from 'context/onboarding-flow/OnboardingFlow';
import {useMenuContext} from 'context/onboarding-menu/OnboardingMenu';
import {ReactComponent as ActionIcon} from 'assets/icons/section/action/edit.svg';
import EfeModalMsg from 'components/efe-modal-msg/EfeModalMsg';

function DocsSign() {
    const history = useHistory();
    const {flow, updateModuleStep} = useFlowContext();
    const {menu, expedientId, fatherKycId} = useMenuContext();
    const {enqueueSnackbar} = useSnackbar();
    const [isLoading, setIsLoading] = React.useState(false);
    const [fields, setFields] = React.useState<Array<Field>>(useFlowFieldSearch());
    const [sign, setSign] = React.useState<any>(undefined);
    const [signed, setSigned] = React.useState(false);
    const {showAlert, defaultError, genericError, closeAlert} = useAlert();

    React.useEffect(() => {
        if (fatherKycId > 0 && ViewKeys.DATA_DOCUMENTATION_SIGNATURE === flow.data.expandKey) {
            setIsLoading(true);
            (async function () {
                try {
                    const response: EfeResponse<Documents> =
                        await DocumentServices.getDocumentByKycId(
                            Constants.DOC_FIRMA_DIGITALIZADA,
                            expedientId,
                            fatherKycId
                        );
                    if (response && response.data) {
                        setSign(response.data.data);
                        setSigned(true);
                    }
                    setIsLoading(false);
                } catch (error) {
                    setIsLoading(false);
                }
            })();
        }
    }, []);

    const accordion: Array<EfeAccordionValues> = [
        {
            icon: <ActionIcon />,
            title: 'Firma Digitalizada',
            reactElement: (
                <Box mt={8}>
                    {!isLoading && (
                        <DocsSignForm
                            fields={fields}
                            submitHandler={onSubmit}
                            sign={sign}
                            signed={signed}
                        />
                    )}
                </Box>
            ),
            expandIcon: true,
            disabled: false,
            expanded: ViewKeys.DATA_DOCUMENTATION_SIGNATURE === flow.data.expandKey,
        },
    ];

    async function onSubmit(values: DigitalizedSignatureValues) {
        setIsLoading(true);
        try {
            const fileConverted = await (await fetch(values.signature)).blob();
            const extension = getBase64Extension(values.signature);
            const request: UploadDocumentServiceRequest = {
                documentKey: Constants.DOC_FIRMA_DIGITALIZADA,
                expedientId: expedientId,
                file: fileConverted,
                kycId: fatherKycId,
                filename: `${Constants.DOC_FIRMA_DIGITALIZADA}.${extension}`,
            };

            const response: EfeResponse<any> = await DocumentServices.updateDocument(request);
            if (response && response.data) {
                enqueueSnackbar(Messages.FILE_UPLOAD_OK, {
                    variant: SnackbarVariants.SUCCESS,
                });
                const path = nextStep(flow, menu);
                if (Constants.MODULE_STEP !== path) {
                    setIsLoading(false);
                    history.push(path);
                } else {
                    updateModuleStep(true);
                    flow.data.expandKey = path;
                    setIsLoading(false);
                }
            } else {
                setFields([...fields]);
                setIsLoading(false);
            }
        } catch (error) {
            setIsLoading(false);
            setFields([...fields]);
            enqueueSnackbar(Messages.FILE_NOT_UPLOAD, {
                variant: SnackbarVariants.ERROR,
            });
        }
    }

    return (
        <MainContent>
            {isLoading && useSpinner(isLoading)}
            {showAlert && (
                <EfeModalMsg
                    error={genericError}
                    open={showAlert}
                    defaultError={defaultError}
                    handleAction={closeAlert}
                />
            )}
            <EfeAccordion
                onChange={() => history.push(HistoryRoutes.DOCS_SIGNATURE)}
                values={accordion}
            />
        </MainContent>
    );
}

export default DocsSign;
